import React from 'react';
import { Card } from '../../../shared/components/ui';
import { Tooltip } from '../../../shared/components/ui';

function TrendArrow({ trend, percent }) {
  if (!trend || trend === 'stable') {
    return (
      <span className="text-gray-400 dark:text-gray-500 text-xs flex items-center gap-1">
        <span>—</span>
        <span>stable</span>
      </span>
    );
  }

  const isUp = trend === 'up';

  return (
    <span className={`text-xs flex items-center gap-1 ${isUp ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {isUp ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        )}
      </svg>
      <span>{percent}%</span>
    </span>
  );
}

function MetricCard({ title, value, suffix, trend, trendPercent, tooltip, onClick, isAlert }) {
  const content = (
    <Card
      className={`p-4 ${onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''} ${isAlert ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
        {tooltip && (
          <Tooltip content={tooltip}>
            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </Tooltip>
        )}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-2xl font-bold ${isAlert ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}>
          {value}
        </span>
        {suffix && (
          <span className={`text-sm ${isAlert ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{suffix}</span>
        )}
      </div>
      {(trend || trendPercent) && (
        <div className="mt-2">
          <TrendArrow trend={trend} percent={trendPercent} />
        </div>
      )}
      {isAlert && onClick && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400 font-medium">Click to review</p>
      )}
    </Card>
  );

  return content;
}

export function PricingHealthCards({ data, onHighRiskClick }) {
  if (!data) return null;

  const { quotesCreated, avgAdjustment, acceptanceRate, highRiskQuotes } = data;

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Pricing Health — Last 30 Days
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Quotes Created"
          value={quotesCreated?.value || 0}
          suffix="this month"
          trend={quotesCreated?.trend}
          trendPercent={quotesCreated?.trendPercent}
        />
        <MetricCard
          title="Avg AI → Final Adjustment"
          value={`+${avgAdjustment?.value || 0}%`}
          tooltip={avgAdjustment?.tooltip}
          trend={avgAdjustment?.trend}
          trendPercent={avgAdjustment?.trendPercent}
        />
        <MetricCard
          title="Acceptance Rate"
          value={`${acceptanceRate?.value || 0}%`}
          tooltip={acceptanceRate?.tooltip}
          trend={acceptanceRate?.trend}
          trendPercent={acceptanceRate?.trendPercent}
        />
        <MetricCard
          title="High-Risk Quotes"
          value={highRiskQuotes?.value || 0}
          suffix="need review"
          isAlert={highRiskQuotes?.value > 0}
          onClick={highRiskQuotes?.value > 0 ? onHighRiskClick : undefined}
        />
      </div>
    </div>
  );
}

export default PricingHealthCards;
